import { httpClient } from "@/shared/api/httpClient";

import type { CreateTimeBlockInput, TimeBlock } from "../model/types";
import type {
  CreateTimeBlockRequestDTO,
  ListTimeBlocksResponseDTO,
  TimeBlockResponseDTO,
} from "./dto";

export function toTimeBlock(dto: TimeBlockResponseDTO): TimeBlock {
  return {
    id: dto.id,
    companyId: dto.company_id,
    masterId: dto.master_id,
    startsAt: dto.starts_at,
    endsAt: dto.ends_at,
    reason: dto.reason,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

/**
 * `GET /api/time-blocks?date=<date>[&master_id=<id>]`
 * Passes `master_id` only when given (omits when undefined).
 * Accepts `signal` for cancellation on rapid day-switch.
 */
export async function listForDay(
  date: string,
  masterId?: string,
  signal?: AbortSignal,
): Promise<TimeBlock[]> {
  const params: Record<string, string> = { date };
  if (masterId !== undefined) {
    params.master_id = masterId;
  }
  const { data } = await httpClient.get<ListTimeBlocksResponseDTO>("/time-blocks", {
    params,
    signal,
  });
  return data.time_blocks.map(toTimeBlock);
}

/** `POST /api/time-blocks` (CSRF) — create a block; 201 → TimeBlock. */
export async function create(input: CreateTimeBlockInput): Promise<TimeBlock> {
  const body: CreateTimeBlockRequestDTO = {
    master_id: input.masterId,
    starts_at: input.startsAt,
    ends_at: input.endsAt,
    ...(input.reason !== undefined ? { reason: input.reason } : {}),
  };
  const { data } = await httpClient.post<TimeBlockResponseDTO>("/time-blocks", body);
  return toTimeBlock(data);
}

/** `DELETE /api/time-blocks/{id}` (CSRF) — remove a block; 204 no body. */
export async function remove(id: string): Promise<void> {
  await httpClient.delete(`/time-blocks/${id}`);
}
